import React, { useState, useEffect } from "react";
import { FaGithub, FaLinkedin, FaBars, FaTimes, FaEnvelope } from "react-icons/fa";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { motion } from "framer-motion";

const TypingText = React.memo(({ messages }) => {
  const [text, setText] = useState("");
  const [messageIndex, setMessageIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentMessage = messages[messageIndex];
    let timeout;

    if (!isDeleting && charIndex === currentMessage.length) {
      timeout = setTimeout(() => setIsDeleting(true), 1000);
    } else if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setMessageIndex((messageIndex + 1) % messages.length);
    } else {
      timeout = setTimeout(() => {
        setCharIndex(charIndex + (isDeleting ? -1 : 1));
        setText(
          currentMessage.substring(0, charIndex + (isDeleting ? -1 : 1))
        );
      }, isDeleting ? 30 : 100);
    }

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, messageIndex]);

  return (
    <p className="text-xl mt-4 font-mono">
      {text}
      <span className="text-blue-600 animate-pulse">|</span>
    </p>
  );
});

const SectionWrapper = React.memo(({ id, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const showUpArrow = id !== "hero";
  const showDownArrow = id !== "projects";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.5 }
    );
    const section = document.getElementById(id);
    if (section) observer.observe(section);
    return () => section && observer.unobserve(section);
  }, [id]);

  const getNextSection = (currentId) => {
    const ids = ["hero", "about", "skills", "experience", "projects"];
    const index = ids.indexOf(currentId);
    return ids[(index + 1) % ids.length];
  };

  const getPreviousSection = (currentId) => {
    const ids = ["hero", "about", "skills", "experience", "projects"];
    const index = ids.indexOf(currentId);
    return ids[(index - 1) % ids.length];
  };

  return (
    <section
      id={id}
      className="bg-gradient-to-b from-white via-slate-50 to-slate-300 pt-16 min-h-screen  flex flex-col justify-center items-center py-20 w-full px-4 text-center transition-all duration-700 ease-in-out relative"
    >
      <div className="w-full max-w-6xl">{children}</div>
      {showDownArrow && isVisible && (
        <a
          href={`#${getNextSection(id)}`}
          className="absolute right-6 top-2/3 transform translate-y-6 animate-bounce text-gray-700 text-3xl"
        >
          <IoIosArrowDown />
        </a>
      )}
      {showUpArrow && isVisible && (
        <a
          href={`#${getPreviousSection(id)}`}
          className="absolute right-6 top-1/3 transform -translate-y-14 animate-bounce text-gray-700 text-3xl"
        >
          <IoIosArrowUp />
        </a>
      )}
    </section>
  );
});

const getExperienceDuration = () => {
  const startDate = new Date("2022-08-01");
  const today = new Date();

  let years = today.getFullYear() - startDate.getFullYear();
  let months = today.getMonth() - startDate.getMonth() + 1;

  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (months == 0) {
    return `${years} year${years !== 1 ? 's' : ''}`;
  }
  else {
    return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`;
  }
};

export default function Portfolio() {
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector("header");
      if (window.scrollY > 10) {
        header?.classList.add("shadow-lg");
      } else {
        header?.classList.remove("shadow-lg");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  const experienceData = [
    {
      title: "Software Engineer II",
      company: "Dell Technologies",
      duration: "Feb 2024 - Present",
      description: [
        "Led development of scalable backend APIs with Node.js.",
        "Integrated Generative AI and LLMs for smart automation.",
        "Automated processes using Python and PowerShell.",
        "Maintained CI/CD pipelines via GitLab.",
        "Collaborated with cross-functional teams for system delivery.",
        "Received the Inspire Award for innovation and performance.",
        "Contributed to fronend development using React.js enhancing full stack project explosure."
      ]
    },
    {
      title: "Software Engineer I",
      company: "Dell Technologies",
      duration: "Aug 2022 - Jan 2024",
      description: [
        "Built backend microservices using Node.js and JWT authentication.",
        "Wrote backend test cases using Jest and optimized PostgreSQL queries.",
        "Managed CI/CD pipelines using GitLab for deployment automation.",
        "Improved DB performance through optimized SQL in PostgreSQL."
      ]
    },
    {
      title: "Intern",
      company: "Tequed Labs",
      duration: "Sep 2021 - Sep 2021",
      description: [
        "Prediction of annual outlay of a user on an e-commerce website using AIML."
      ]
    },
    {
      title: "Intern",
      company: "Dell Technologies",
      duration: "Jun 2021 - Jul 2021",
      description: [
        "Supported a web application by integrating new features using Angular."
      ]
    },
  ];

  const projectData = [
    {
      name: "Resume-Skill-Extractor-Job-Match-App",
      description: "An application that extracts skills from resumes and return best-matching jobs based on how many skills overlap.",
      link: "https://github.com/navyashree2000/Resume-Skill-Extractor-Job-Match-App"
    },
    {
      name: "Sensitive Data Detector for GitHub Commits (Under development)",
      description: "A tool that detects sensitive data leaks such as API keys and credentials in GitHub commit history.",
      link: "https://github.com/navyashree2000/SensitiveData-Detector-for-GitHub-Commits/tree/dev"
    }
  ];

  const skillData = ['Node JS', 'Python', 'SQL', 'TypeScript', 'Powershell', 'AWS(S3)', 'Azure(VM)', 'HTML', 'CSS', 'JavaScript', 'React', 'Gen AI(LLM)', 'PostgreSQL', 'MSSQL', 'Redis', 'Git', 'CI/CD', 'RabbitMQ', 'HashiCorp Vault', 'PCF', 'JIRA'];

  const messages = [
    "Full Stack Developer",
    "I Automate process with Generative AI & LLMs",
    "Tech Enthusiast",
  ];

  return (
    <div className="min-h-screen font-sans text-black scroll-smooth">
      <style>{`
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
      `}</style>
      <header className="bg-white sticky top-0 z-20 transition-shadow backdrop-blur bg-opacity-80">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* <h1 className="text-3xl font-bold">Sanath </h1> */}
          <div className="md:hidden text-2xl cursor-pointer" onClick={toggleMenu}>
            {menuOpen ? <FaTimes /> : <FaBars />}
          </div>
          <nav className="space-x-6 hidden md:flex text-gray-700 font-medium">
            {["about", "skills", "experience", "projects"].map((section) => (
              <a key={section} href={`#${section}`} className="hover:text-blue-500 capitalize">
                {section}
              </a>
            ))}
          </nav>

        </div>
        {menuOpen && (
          <div className="md:hidden px-6 pb-4 space-y-2 text-gray-700 font-medium bg-white bg-opacity-90">
            {["about", "skills", "experience", "projects"].map((section) => (
              <a key={section} href={`#${section}`} onClick={closeMenu} className="block hover:text-blue-500 capitalize">
                {section}
              </a>
            ))}

          </div>
        )}
      </header>

      <main>

        {/*  <SectionWrapper id="hero">
          <motion.div
            className="flex flex-col items-center text-center py-20 px-4 bg-gradient-to-b from-white to-blue-50 min-h-screen justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-4xl sm:text-5xl font-extrabold text-gray-800 leading-tight"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Hi, I’m <span className="text-blue-600">Navyashree S</span>
            </motion.h1>

            <motion.p
              className="text-lg sm:text-xl mt-4 text-gray-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <TypingText messages={messages} />
            </motion.p>

            <motion.div
              className="flex justify-center gap-6 mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <div className="flex items-center gap-2 text-gray-800">
                <a href="https://linkedin.com/in/s-navyashree" className="text-blue-600 hover:text-blue-800 transition-transform transform hover:scale-110" target="_blank" rel="noopener noreferrer">
                  <FaLinkedin className="text-3xl" />
                </a>
                <span className="text-sm">Navyashree</span>
              </div>
              <div className="flex items-center gap-2 text-gray-800">
                <a href="https://github.com/yourusername" className="text-gray-600 hover:text-black transition-transform transform hover:scale-110" target="_blank" rel="noopener noreferrer">
                  <FaGithub className="text-3xl" />
                </a>
                <span className="text-sm">Navyashree</span>
              </div>
              <div className="flex items-center gap-2 text-gray-800">
                <a href="mailto:mysnavyashrees@gmail.com" className="text-red-500 hover:text-red-700 transition-transform transform hover:scale-110" target="_blank" rel="noopener noreferrer">
                  <FaEnvelope className="text-3xl" />
                </a>
                <span className="text-sm">mysnavyashrees@gmail.com</span>
              </div>
            </motion.div>

            <motion.a
              href="#about"
              className="mt-12 text-blue-500 animate-bounce text-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              ↓
            </motion.a>
          </motion.div>
        </SectionWrapper> */}

        <SectionWrapper id="hero">
          <h1 className="text-5xl font-bold">Hi, I’m <span className="text-blue-600">Navyashree S</span></h1>
          <TypingText messages={messages} />
          <div className="flex justify-center gap-6 mt-6">
            <div className="flex items-center gap-2 text-gray-800">
              <a href="https://linkedin.com/in/s-navyashree" className="text-blue-600 hover:text-blue-800 transition-transform transform hover:scale-110" target="_blank" rel="noopener noreferrer">
                <FaLinkedin className="text-3xl" />
              </a>
              {/* <span className="text-sm">Navyashree</span> */}
            </div>
            <div className="flex items-center gap-2 text-gray-800">
              <a href="https://github.com/yourusername" className="text-gray-600 hover:text-black transition-transform transform hover:scale-110" target="_blank" rel="noopener noreferrer">
                <FaGithub className="text-3xl" />
              </a>
              {/* <span className="text-sm">Navyashree</span> */}
            </div>
            <div className="flex items-center gap-2 text-gray-800">
              <a href="mailto:mysnavyashrees@gmail.com" className="text-red-500 hover:text-red-700 transition-transform transform hover:scale-110" target="_blank" rel="noopener noreferrer">
                <FaEnvelope className="text-3xl" />
              </a>
              <span className="text-sm">mysnavyashrees@gmail.com</span>
            </div>
          </div>
        </SectionWrapper>

        <SectionWrapper id="about">
          <h2 className="text-3xl font-bold mb-6">About Me</h2>
          <motion.div
            className="flex flex-col md:flex-row items-center gap-8 max-w-5xl mx-auto py-20 px-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img
              src="/Navya.jpeg"
              alt="Navyashree"
              className="w-56 rounded-full object-cover shadow-lg"
            />
            <div className="text-lg text-left text-gray-700 space-y-4">
              <p>
                I’m a software developer with <span className="font-semibold text-blue-600">{getExperienceDuration()} of experience</span> in building full-stack applications, with a strong focus on
                <span className="font-semibold text-blue-600"> backend development</span>. I specialize in
                <span className="font-semibold text-indigo-600"> Node.js</span>,
                <span className="font-semibold text-indigo-600"> Python</span>, and
                <span className="font-semibold text-indigo-600"> PowerShell</span>, and have built
                <span className="font-semibold text-blue-600"> scalable APIs</span> and backend systems from the ground up.
              </p>
              <p>
                On the frontend, I work with <span className="font-semibold text-indigo-600">React.js</span> and have hands-on experience with cloud platforms like
                <span className="font-semibold text-indigo-600"> AWS (S3)</span> and
                <span className="font-semibold text-indigo-600"> Azure VMs</span>. I’m also experienced in
                <span className="font-semibold text-indigo-600"> PCF</span> for deployments.
              </p>
              <p>
                I’ve implemented and maintained <span className="font-semibold text-blue-600">CI/CD pipelines</span> as part of
                <span className="font-semibold text-blue-600"> DevOps workflows</span>, ensuring smooth and reliable releases.
              </p>
              <p>
                I’ve worked with <span className="font-semibold text-indigo-600">Generative AI</span> and
                <span className="font-semibold text-indigo-600"> Large Language Models (LLMs)</span> to automate tasks, improve workflows, and enhance system intelligence.
              </p>
              <p>
                I enjoy collaborating with <span className="font-semibold text-blue-600">cross-functional teams</span> and building
                <span className="font-semibold text-blue-600"> high-impact, reliable solutions</span>.
              </p>
            </div>
          </motion.div>
        </SectionWrapper>

        <SectionWrapper id="skills">
          <h2 className="text-3xl font-bold mb-6">Skills</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 text-center">
            {skillData.map((skill, idx) => (
              <div
                key={idx}
                className="bg-white/60 p-4 rounded-xl shadow text-black font-medium fade-in-up"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                {skill}
              </div>
            ))}
          </div>
        </SectionWrapper>

        <SectionWrapper id="experience">
          <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
            Professional Experience
          </h2>

          <div className="max-w-4xl mx-auto h-[500px] overflow-y-auto pr-2">
            <div className="relative border-l-4 border-blue-500 pl-6 space-y-10">
              {experienceData.map((exp, idx) => (
                <div key={idx} className="relative">
                  {/* Timeline Dot */}
                  <div className="absolute -left-3 top-1 w-6 h-6 bg-blue-500 rounded-full border-4 border-white z-10 shadow" />

                  {/* Experience Card */}
                  <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6 relative">
                    {/* Duration - shown in top-right on medium and larger screens */}
                    <div className="hidden md:block absolute top-6 right-6 text-sm text-gray-500 font-medium">
                      {exp.duration}
                    </div>

                    {/* Title and Company */}
                    <h3 className="text-xl font-semibold text-blue-700">{exp.title}</h3>
                    <p className="text-sm text-indigo-600 mb-2">{exp.company}</p>

                    {/* Duration - inline on mobile screens */}
                    <div className="block md:hidden text-sm text-gray-500 font-medium mb-2">
                      {exp.duration}
                    </div>

                    {/* Bullet Points */}
                    <div className="space-y-2 text-sm text-gray-700">
                      {exp.description.map((point, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-blue-600 mt-1">•</span>
                          <span className="leading-relaxed">{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </SectionWrapper>


        <SectionWrapper id="projects">
          <h2 className="text-3xl font-bold mb-6">Personal Projects</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {projectData.map((proj, idx) => (
              <div
                key={idx}
                className="bg-white/70 backdrop-blur p-6 rounded-xl shadow opacity-0 translate-y-6 transition-all duration-700 ease-in-out"
                style={{ animation: `fadeInUp 0.6s ease-out forwards`, animationDelay: `${idx * 0.2}s` }}
              >
                <h3 className="text-xl font-semibold">{proj.name}</h3>
                <p className="mt-2">{proj.description}</p>
                <a href={proj.link} className="text-blue-600 hover:underline mt-2 inline-block" target="_blank" rel="noopener noreferrer">
                  View on GitHub
                </a>
              </div>
            ))}
          </div>
          <style>{`
          @keyframes fadeInUp {
            0% { opacity: 0; transform: translateY(30px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        </SectionWrapper>
      </main>
    </div>
  );
}
